import React, { useState, useRef, ReactNode } from 'react';
import { Form, Message } from '@bedrock/components';
import { useOpenModal } from 'utils/openModal';
import { useMemoizedFn } from 'ahooks';
import _ from 'lodash';
import SFormItem from '@mpc/common/src/components/SForm/SFormItem';

const zIndex = 1201;

export interface BackModalConfig {
  title?: string;
  label?: string;
  topShow?: ReactNode;
  onCommit?: ({ reason }: any) => any;
  isRequired?: boolean;
  isSbumit?: boolean;
  okText?: any;
}

export const useSendBackModal = () => {
  const [loading, setLoading] = useState(false);
  const [
    {
      title,
      label,
      onCommit,
      topShow = null,
      isRequired = true,
      isSbumit = false,
      okText,
    },
    setModalParams,
  ] = useState({
    title: '废弃',
    label: '废弃原因',
    onCommit: ({ reason }: { reason: string }) => {},
    topShow: null,
    isRequired: true,
  });
  const timer = useRef(null);
  const [form] = Form.useForm();
  const { validateFields, resetFields } = form;
  const { open, destroy } = useOpenModal({
    title,
    width: 480,
    height: 304,
    okButtonProps: {
      loading,
      type: 'primary',
      danger: okText || title === '拒绝' ? false : true,
    },
    zIndex,
    okText: okText
      ? okText
      : title === '拒绝'
      ? isSbumit
        ? '提交'
        : '确定拒绝'
      : '确定退回',
    onOk: async () => {
      if (timer) clearTimeout(timer.current);
      const values = await validateFields();
      const { reason } = values;
      if (!reason) {
        if (!isRequired) {
          return Promise.resolve(onCommit(values))
            .then(() => {
              destroy();
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          Message.error({
            content:
              title === '拒绝'
                ? '拒绝失败，请填写拒绝原因'
                : '退回失败，请填写退回原因',
            colored: true,
            duration: 3,
            closable: true,
          });
        }
        return Promise.reject();
      } else {
        setLoading(true);
        return Promise.resolve(onCommit(values))
          .then(() => {
            destroy();
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    children: (
      <Form layout="vertical" showLabelColon form={form}>
        {topShow}
        <SFormItem 
          formItemKey={'reason'}
          label={label}
          required={isRequired}
          componentClass='textarea'
          elementProps={{
            maxLength: 200,
            showCount: true,
            rows: 3,
            style: {
              width: '100%'
            }
          }}
        >
        </SFormItem>
      </Form>
    ),
  });

  const sendBackModal = useMemoizedFn(
    (
      config: BackModalConfig = {}
    ) => {
      resetFields();
      setModalParams({
        title: '废弃',
        label: '废弃原因',
        topShow: null,
        onCommit: ({ reason }) => {},
        isRequired: true,
        ...config,
      });
      open();
    }
  );
  return sendBackModal;
};


// 创建工具函数
export const createAsyncAction = (
  loading: boolean,
  setLoading: (loading: boolean) => void, 
  callback?: () => void,
) => {
  return async(
    action: any, 
    options?: {
      onSuccess?: (result: any) => void;
      onError?: (error: unknown) => void;
      onFinally?: () => void;
    }
  ): Promise<any | void> => {
    if (loading) return;
    
    setLoading(true);
    try {
      const result = await action();
      options?.onSuccess?.(result);
      await callback?.();
      return result;
    } catch (error) {
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
      options?.onFinally?.();
    }
  };
}
